import { computed, effect, Injectable } from "@angular/core";
import { patchState, signalStore, withState } from "@ngrx/signals";
import { type MenuItem, MenuService } from "../services/menu.service";
import { UserStore } from "./user.store";
import { withRequestStatus } from "./features/with-request-status.feature";
import { toObservable } from "@angular/core/rxjs-interop";
import { asyncScheduler, firstValueFrom, observeOn } from "rxjs";

const MENU_STORAGE_KEY = "menu";

interface MenuState {
  items: MenuItem[];
}

const initialState: MenuState = {
  items: [],
};

@Injectable({ providedIn: "root" })
export class MenuStore extends signalStore({ protectedState: false }, withState<MenuState>(initialState), withRequestStatus()) {
  country$ = toObservable(this.userStore.user.country).pipe(observeOn(asyncScheduler));
  readonly hasAdditionalItems = computed(() => this.items().some((item: MenuItem) => item.key !== "home"));
  private loaded = false;

  constructor(
    private readonly menuService: MenuService,
    private readonly userStore: UserStore
  ) {
    super();
    this.clearMenuOnLogout();
  }

  initialize(): void {
    this.country$.subscribe((country: string) => {
      if (!!country && country.trim() !== "") {
        void this.loadMenu();
      }
    });
  }

  async loadMenu(): Promise<void> {
    if (this.loaded) return;

    const storedMenu = this.getStoredMenu();

    if (storedMenu) {
      patchState(this, { items: storedMenu });
      this.loaded = true;
      return;
    }

    const menuItems = (await firstValueFrom(this.menuService.getMenu())) ?? [];
    patchState(this, { items: menuItems });
    this.saveMenu(menuItems);
    this.loaded = true;
  }

  hasItem(key: string): boolean {
    return this.items().some((item: MenuItem) => item.key === key);
  }

  private getStoredMenu(): MenuItem[] | undefined {
    try {
      const storedMenu = sessionStorage.getItem(MENU_STORAGE_KEY);
      if (!storedMenu) return undefined;

      const parsed = JSON.parse(storedMenu) as unknown;
      if (!Array.isArray(parsed)) return undefined;
      if (!parsed.every((item: unknown): item is MenuItem => this.isMenuItem(item))) return undefined;

      return parsed;
    } catch {
      return undefined;
    }
  }

  private isMenuItem(item: unknown): item is MenuItem {
    return !!item && typeof item === "object" && "key" in item && typeof item.key === "string" && "isActive" in item && typeof item.isActive === "boolean";
  }

  private saveMenu(menuItems: MenuItem[]): void {
    try {
      sessionStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(menuItems));
    } catch {
      // Storage can fail in restricted browser contexts; menu remains available in memory.
    }
  }

  private clearMenuOnLogout(): void {
    effect(() => {
      if (!this.userStore.isAuthenticated()) {
        patchState(this, initialState);
        this.loaded = false;
        sessionStorage.removeItem(MENU_STORAGE_KEY);
        console.log("Menu cleared due to logout/expiration");
      }
    });
  }
}
